import { CK_ENV } from 'ck-core/source/data/ckEnv';

import './index.less';

import _ from 'lodash';
import React from 'react';
import { StripeProvider, Elements } from 'react-stripe-elements';

import Errors from 'ck-core/source/services/Errors';
import Language from 'ck-core/source/services/Language';
import IntercomManager from 'ck-core-client/source/managers/IntercomManager';
import UtmTrackingService from 'ck-core-client/source/services/UtmTrackingService';
import subscriptionPlans from 'ck-core/source/collections/subscriptionPlans';
import UserManager from 'ck-core-client/source/managers/UserManager';

import langToReactElements from 'ck-core-client/source/functions/langToReactElements';

import PaypalButton from 'ck-website/source/ui/newPayments/PaypalButton';
import CurrencyChoice from 'ck-website/source/ui/newPayments/CurrencyChoice';
import { lang } from './lang';
import StripePaymentButton from 'ck-website/source/ui/newPayments/StripePaymentButton';
import { TabbedSelector } from 'ck-core-client/source/components/TabbedSelector';
import { TabTheme } from 'ck-core-client/source/components/TabbedSelector/Tab';
import EmailInput from 'ck-core-client/source/ui/form/EmailInput';
import { CURRENCY } from 'ck-core/source/data/Currency';
import { trackAnalytic } from 'ck-core-client/source/trackers/Analytic';
import { AppContext, withAppContext } from 'ck-core-client/source/context';
import { poll } from 'ck-core/source/functions/poll';
import { ADWORDS_CONVERSION_LABEL } from 'ck-core-client/source/trackers/Adwords';
import { FACEBOOK_STANDARD_EVENT } from 'ck-core-client/source/trackers/Facebook';
import { TIKTOK_STANDARD_EVENT } from 'ck-core-client/source/trackers/TikTok';
import { HOTJAR_STANDARD_EVENT } from 'ck-core-client/source/trackers/Hotjar';

import checkoutCreditCardIcon from 'ck-core-client/graphics/checkout/checkout-credit-card-icon.svg';
import applePayIcon from 'ck-core-client/graphics/paymentIcons/applePay.svg';
import paypalIcon from 'ck-core-web/graphics/generic/paypal.svg';
import { ConnectedCreditCardForm } from 'ck-website/source/components/CreditCardForm';

export enum BILLING_METHOD {
	STRIPE = 'stripe',
	BRAINTREE = 'braintree',
	STRIPE_BUTTON = 'stripeButton'
}

// TODO: Refactor this so we explicitly pass one of the provider responses (this interface would fit an empty object)
export interface PaymentTokenData {
	stripeResponse?: {
		id: string;
	};
	braintreeResponse?: {
		nonce: string;
		details?: {
			email?: string;
		};
	};
	braintreeDeviceData?: any;
}

export interface PaymentFormActions {
	handleSubmit: (tokenData: PaymentTokenData, deferredSuccess?: boolean) => Promise<void>;
	setPaymentSubmitting: (isSubmitting: boolean) => void;
	trackAnalytic: typeof trackAnalytic;
}

interface UpdateDetailsProps extends PaymentFormActions {
	kind: 'updateDetails';
	isUpdateDetails: boolean;
}

interface PaymentProps extends PaymentFormActions {
	kind: 'payment';
	email?: string;
	price: number;
	priceInCurrency: any;
	currency: CURRENCY;
	coupon?: any;
	planId: string;
	isSubscriptionChange?: boolean;
}

export type PaymentMethodProps = PaymentProps | UpdateDetailsProps;

interface Props {
	appContext: AppContext;

	billingMethod: BILLING_METHOD;
	handleSubmit: (result: any, request: any) => Promise<any>;
	handleCurrencyChange?: (currency: CURRENCY) => void;

	isUpdateDetails?: boolean;
	wasPastDue?: boolean;
	hasHadAPlan?: boolean;

	allowCurrencyChange?: boolean;

	currency: CURRENCY;
	planId?: string;
	coupon?: any;

	autoRegisteredSession?: any;

	giftSelected?: boolean;
	validCardExpected?: boolean;

	email?: string;

	paymentDate?: Date;

	changeSubscriptionMessage?: string;
}

interface State {
	stripe?: any;
	canMakeStripeButtonPayment: boolean | null;
	stripeButtonEmail: string;
	stripeButtonEmailError: string;
	paymentSubmitting: boolean;
	paymentSubmittingStartTime?: number;

	braintreeError?: any;

	showApplePayMessage?: boolean;
}

export class PaymentForm extends React.Component<Props, State> implements PaymentFormActions {
	constructor(props: Props) {
		super(props);

		this.state = {
			stripe: undefined,
			canMakeStripeButtonPayment: null,
			stripeButtonEmail: '',
			stripeButtonEmailError: '',
			paymentSubmitting: false
		};
	}

	componentDidMount() {
		this.waitForStripe();
	}

	async waitForStripe() {
		if (typeof window !== 'undefined') {
			if (window.Stripe) {
				this.setState({
					stripe: window.Stripe(CK.config.environment.STRIPE_PUBLISHABLE_KEY)
				});

				return;
			}
			await poll(() => !!window.Stripe, {
				timeout: 20000
			});
			this.setState({
				stripe: window.Stripe(CK.config.environment.STRIPE_PUBLISHABLE_KEY)
			});
		} else {
			// Stub
			this.setState({
				stripe: {
					elements: () => ({
						create: () => ({
							mount: () => {},
							update: () => {},
							destroy: () => {},
							on: () => {}
						})
					}),
					createSource: () => {},
					createToken: () => {},
					createPaymentMethod: () => {},
					handleCardPayment: () => {}
				}
			});
		}
	}

	handleCurrencyChange = (currency: CURRENCY) =>
		this.props.handleCurrencyChange && this.props.handleCurrencyChange(currency);

	trackAnalytic: typeof trackAnalytic = (event, data) => {
		this.props.appContext.trackers.trackAnalytic(
			event,
			_.extend(
				{
					isApplePay: !!this.state.stripeButtonEmail,
					isUpdateDetails: this.props.isUpdateDetails,
					currency: this.props.currency,
					plan: this.props.planId,
					coupon: this.props.coupon ? this.props.coupon.id() : null,
					delay: this.state.paymentSubmittingStartTime
						? Date.now() - this.state.paymentSubmittingStartTime
						: null,
					giftSelected: this.props.giftSelected,
					autoRegisterConversion: !!this.props.autoRegisteredSession
				},
				data
			)
		);
	};

	async updatePaymentSource(paymentDetails) {
		let endpoint, request;

		if (paymentDetails.stripePaymentMethod) {
			endpoint = 'updateStripePaymentMethod';

			request = {
				stripePaymentMethod: paymentDetails.stripePaymentMethod
			};
		} else {
			endpoint = 'updateBraintreePaymentSource';

			request = {
				braintreeNonce: paymentDetails.braintreeNonce,
				braintreeDeviceData: paymentDetails.braintreeDeviceData
			};
		}

		const MessageModal = require('ck-core-client/source/ui/modals/MessageModal');

		await CK.clientSockets.users.emit(endpoint, request);

		MessageModal.create({
			title: lang.UPDATE_SOURCE.SUCCESS.title,
			message: this.props.wasPastDue
				? lang.UPDATE_SOURCE.SUCCESS.messageWasPastDue
				: lang.UPDATE_SOURCE.SUCCESS.message,
			actions: {},
			titleBgColour: 'green'
		});
	}

	handleSubmit = async (paymentTokenData: PaymentTokenData, deferredSuccess = false) => {
		const stripeResponse = _.get(paymentTokenData, 'stripeResponse');
		const stripePaymentMethod = _.get(stripeResponse, 'id');
		const braintreeNonce = _.get(paymentTokenData, 'braintreeResponse.nonce');
		const braintreeDeviceData = _.get(paymentTokenData, 'braintreeDeviceData');
		if (this.props.isUpdateDetails) {
			return this.updatePaymentSource({
				stripePaymentMethod,
				braintreeNonce,
				braintreeDeviceData
			});
		}

		const email =
			_.get(paymentTokenData, 'braintreeResponse.details.email') ||
			paymentTokenData.email ||
			this.state.stripeButtonEmail;

		const request = _.omitBy(
			{
				trackingData: CK.trackingData,
				utm: UtmTrackingService.mod().getContext(),
				email,
				stripePaymentMethod,
				braintreeNonce,
				planId: this.props.planId,
				currency: this.props.currency,
				coupon: this.props.coupon ? this.props.coupon.id() : null,
				braintreeDeviceData,
				isCoolmath: CK.config.name === 'ck-coolmath'
			},
			_.isNil
		);

		// See https://developers.braintreepayments.com/reference/general/testing
		if (CK.config.environment.name !== CK_ENV.PRODUCTION && window.braintreeForcedPaymentAmount) {
			request.forcedPaymentAmount = window.braintreeForcedPaymentAmount;
		}

		let autoRegisteredSession = this.props.autoRegisteredSession;

		try {
			let result;

			const authenticated = CK.user.$exists() && !CK.user.shouldConvertAsAutoRegistered();

			if (authenticated) {
				result = await CK.clientSockets.users.emit('buyAuthenticated', request);
			} else {
				const userManager = UserManager.mod();

				// CORE-1333: If a user visits the editor and autoregisters, then sets up a username, but somehow loses their session
				// set up a new autoRegisteredSession to make this work. This should not happen with the other changes in this commit
				// so log a warning if it does.
				if (!autoRegisteredSession && CK.user.shouldConvertAsAutoRegistered()) {
					CK.logger.warn('autoRegistered session went AWOL', {
						user: CK.user.$()
					});

					autoRegisteredSession = userManager.getSession();
					autoRegisteredSession.setupUsername = CK.user.name();
					autoRegisteredSession.autoRegistered = true;
				}

				if (autoRegisteredSession) {
					// If they visited the editor and were autoRegistered, reconstruct trackingData based on the autoRegistered
					// user's trackingData, which is what the person first had when they visited us.
					request.trackingData = request.trackingData || {
						testCohorts: _.keyBy(CK.user.testCohorts().$(), 'id'),
						testSeed: CK.user.testSeed(),
						id: CK.user.trackingId()
					};

					if (autoRegisteredSession.setupUsername && !this.props.giftSelected) {
						request.session = autoRegisteredSession;

						await userManager.remoteLogout(request.session.userId);
					}
				}

				result = await userManager.authenticateWithEndpoint('buyLoggedOut', request, null, {
					waitForVerify: false
				});
			}

			// This is used to allow the child to control transitioning to a success state
			if (deferredSuccess) {
				return { request, result };
			} else {
				return this.handleSuccess(request, result);
			}
		} catch (err) {
			if (err instanceof Errors.CardDeclined || err instanceof Errors.BraintreeDeclined) {
				const declineCode = _.get(err.tags, 'declineCode');
				err.message = Language.mapVariables(lang.FAILED.MESSAGE, {
					message: err.message + (declineCode ? `<br>Decline Code: ${declineCode}` : ''),
					prepaidCardMessage:
						_.get(stripeResponse, 'card.funding') === 'prepaid' ? lang.FAILED.PREPAID : ''
				});

				this.trackAnalytic(err.name, {
					externalBilling: braintreeNonce ? CK.const.EXTERNAL_BILLING_SOURCE.BRAINTREE : undefined,
					email: email,
					message: err.message
				});

				if (err instanceof Errors.CardDeclined) {
					try {
						IntercomManager.mod().emptyUpdate();
					} catch (err) {
						CK.logger.warn(err);
					}
				}
			} else if (err instanceof Errors.PaidSubscriptionAlreadyActive) {
				err.message = lang.ALREADY_PAID.MESSAGE;

				this.trackAnalytic('AlreadyPaid', {
					email: email
				});
			} else {
				this.trackAnalytic('PaymentFailure', {
					message: err.message
				});
			}

			if (braintreeNonce) {
				this.setState({
					braintreeError: _.get(err, 'message')
				});
			}

			if (autoRegisteredSession) {
				UserManager.mod().verifySession(autoRegisteredSession);
			}

			throw err;
		}
	};

	private handleSuccess = async (request: any, result: any) => {
		this.setPaymentSubmitting(false);
		this.trackAnalytic('Purchase', {
			externalBilling: request.braintreeNonce ? CK.const.EXTERNAL_BILLING_SOURCE.BRAINTREE : ''
		});
		this.props.appContext.trackers.trackAdwordsConversion(ADWORDS_CONVERSION_LABEL.AUTHORISE);

		const plan = subscriptionPlans(this.props.planId);
		const priceInCurrency = plan.priceInCurrency(this.props.currency, {
			coupon: this.props.coupon,
			user: CK.user
		});
		try {
			window.gtag('event', 'complete_purchase', {
				planId: this.props.planId,
				currency: this.props.currency,
				value: priceInCurrency.price
			});
		} catch (e) {
			CK.logger.debug('Error occurred with gtag');
		}

		this.props.appContext.trackers.trackFacebookPixel(
			FACEBOOK_STANDARD_EVENT.PURCHASE,
			{
				value: priceInCurrency.price / 100,
				currency: _.upperCase(this.props.currency),
				planId: this.props.planId
			},
			{
				eventID: CK.user ? CK.user.trackingId() : CK.trackingData ? CK.trackingData.id : 'unknown'
			}
		);

		this.props.appContext.trackers.trackTiktokPixel(TIKTOK_STANDARD_EVENT.PURCHASE);

		this.props.appContext.trackers.trackHotjarPixel(HOTJAR_STANDARD_EVENT.PURCHASE);

		/* eslint-disable */

		try {
			let AWIN = window.AWIN;

			let orderRef = result.model.id;

			AWIN.Tracking = {};
			AWIN.Tracking.Sale = {};
			/*** Set your transaction parameters ***/
			AWIN.Tracking.Sale.amount = priceInCurrency.price / 100;
			AWIN.Tracking.Sale.orderRef = orderRef;
			AWIN.Tracking.Sale.parts = 'DEFAULT:' + priceInCurrency.price / 100;
			AWIN.Tracking.Sale.voucher = this.props.coupon;
			AWIN.Tracking.Sale.currency = _.upperCase(this.props.currency);
			AWIN.Tracking.Sale.channel = 'aw';

			let awPixel = new Image(0, 0);
			awPixel.src =
				'https://www.awin1.com/sread.img?tt=ns&tv=2&merchant=32629&amount=' +
				parseFloat(priceInCurrency.price / 100).toFixed(2) +
				'&ch=aw&parts=DEFAULT:' +
				parseFloat(priceInCurrency.price / 100).toFixed(2) +
				'&ref=' +
				orderRef +
				'&vc=' +
				this.props.coupon +
				'&cr=' +
				_.upperCase(this.props.currency) +
				'&testmode=0';

			document.body.appendChild(awPixel);
		} catch (err) {}

		/* eslint-enable */

		this.props.handleSubmit && this.props.handleSubmit(result, request);
	};

	/**
	 * Warn the user about closing the window during payment submission.
	 *
	 * See https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
	 */
	beforeUnloadHandler(event: BeforeUnloadEvent) {
		// Cancel the event as stated by the standard.
		event.preventDefault();
		// Chrome requires returnValue to be set.
		event.returnValue = '';
	}

	setPaymentSubmitting = (paymentSubmitting: boolean) => {
		if (paymentSubmitting === this.state.paymentSubmitting) {
			return;
		}

		if (paymentSubmitting) {
			window.addEventListener('beforeunload', this.beforeUnloadHandler);
		} else {
			window.removeEventListener('beforeunload', this.beforeUnloadHandler);
		}

		this.setState(prevState =>
			Object.assign(prevState, {
				paymentSubmitting,
				paymentSubmittingStartTime: paymentSubmitting ? Date.now() : null
			})
		);
	};

	allowPaypal(plan) {
		const okForPlan = !plan || (plan && (plan.oneOff() || plan.intervalInMonths() >= 12));
		const noUser = !CK.user || !CK.user.$exists();
		const noPayments = CK.user ? !CK.user.paymentCurrency() : true;
		const isPaypalUser = CK.user
			? CK.user.externalBilling() === CK.const.EXTERNAL_BILLING_SOURCE.BRAINTREE
			: false;

		return okForPlan && (noUser || noPayments || isPaypalUser);
	}

	allowStripe() {
		const noUser = !CK.user || !CK.user.$exists();

		return noUser || !CK.user.externalBilling();
	}

	applePayAvailable() {
		return typeof window !== 'undefined' && !!window.ApplePaySession;
	}

	render() {
		let plan, priceInCurrency;

		if (this.props.planId) {
			plan = subscriptionPlans(this.props.planId);
			priceInCurrency = plan.priceInCurrency(this.props.currency, {
				coupon: this.props.coupon,
				html: false,
				user: CK.user
			});
		}

		const paymentMethodProps = _.omitBy(
			{
				isUpdateDetails: this.props.isUpdateDetails,
				price: _.get(priceInCurrency, 'price'),
				priceInCurrency,
				planId: this.props.planId,
				currency: this.props.currency,
				coupon: this.props.coupon,
				handleSubmit: this.handleSubmit,
				paymentSubmitting: this.state.paymentSubmitting,
				setPaymentSubmitting: this.setPaymentSubmitting,
				trackAnalytic: this.trackAnalytic,
				validCardExpected: this.props.validCardExpected,
				email: this.props.email,
				paymentDate: this.props.paymentDate,
				isSubscriptionChange: !!this.props.changeSubscriptionMessage
			},
			_.isNil
		);

		const allowPaypal = this.allowPaypal(plan);
		const allowStripe = this.allowStripe();

		const canMakeStripeButtonPayment = stripePaymentRequest => {
			const paymentRequest = stripePaymentRequest({
				country: 'GB',
				currency: paymentMethodProps.currency,
				total: {
					label: paymentMethodProps.planId,
					amount: paymentMethodProps.price
				}
			});

			paymentRequest.canMakePayment().then(result => {
				CK.logger.debug('PaymentRequest.canMakePayment result', { result });

				const forceAllow = CK.config.environment.name !== CK_ENV.PRODUCTION;
				if (forceAllow) {
					CK.logger.debug('Allowing canMakeStripeButtonPayment on dev environment');
				}
				this.setState({
					canMakeStripeButtonPayment: !!result || forceAllow
				});
			});
		};

		const shouldShowPaymentRequestBrowser = () => {
			return this.applePayAvailable() && this.allowStripe();
		};

		if (
			this.state.stripe &&
			shouldShowPaymentRequestBrowser() &&
			this.state.canMakeStripeButtonPayment === null
		) {
			canMakeStripeButtonPayment(
				window.Stripe(CK.config.environment.STRIPE_PUBLISHABLE_KEY).paymentRequest
			);
		}

		const currencyChoices = !this.props.isUpdateDetails && this.props.allowCurrencyChange && (
			<CurrencyChoice
				currency={paymentMethodProps.currency}
				handleChange={this.handleCurrencyChange}
				disabled={this.state.paymentSubmitting}
			/>
		);
		const choices = [
			allowStripe
				? {
						tab: {
							id: BILLING_METHOD.STRIPE,
							icon: checkoutCreditCardIcon,
							title: 'Card',
							colour: 'lightGrey'
						},
						content: (
							<React.Fragment>
								{currencyChoices}
								<ConnectedCreditCardForm
									handleSuccess={this.handleSuccess}
									{...paymentMethodProps}
								/>
							</React.Fragment>
						)
				  }
				: null,
			allowPaypal
				? {
						tab: { id: BILLING_METHOD.BRAINTREE, icon: paypalIcon, colour: 'lightGrey' },
						content: (
							<div>
								{currencyChoices}
								<p>{lang.COMPLETE_WITH_PAYPAL}</p>
								{this.state.braintreeError && (
									<div className="PaymentForm__errorMessage">{this.state.braintreeError}</div>
								)}
								<PaypalButton
									{...paymentMethodProps}
									ref={e => (this.paypalButton = e)}
									handleError={error => {
										this.setState({ braintreeError: _.get(error, 'message') });
									}}
								/>
							</div>
						)
				  }
				: null,
			shouldShowPaymentRequestBrowser()
				? {
						tab: {
							id: BILLING_METHOD.STRIPE_BUTTON,
							title: 'Apple Pay',
							icon: applePayIcon,
							colour: 'lightGrey',
							handleClick: () => {
								this.trackAnalytic('ApplePayTabSelected');
								!this.state.canMakeStripeButtonPayment &&
									this.setState({
										showApplePayMessage: true
									});
							},
							disabled: !this.state.canMakeStripeButtonPayment
						},
						content: (
							<React.Fragment>
								{currencyChoices}
								<EmailInput
									label="Cardholder email"
									name="email"
									hasAsterisk={true}
									value={this.state.stripeButtonEmail}
									onChange={input => {
										this.setState({ stripeButtonEmail: input });
									}}
									onValidate={(valid, error) => {
										this.setState({ stripeButtonEmailError: valid ? null : error });
									}}
									error={this.state.stripeButtonEmailError}
								/>
								<StripePaymentButton
									{...paymentMethodProps}
									handleSuccess={this.handleSuccess}
									validEmail={this.state.stripeButtonEmail && !this.state.stripeButtonEmailError}
									handleInvalidClick={() => {
										this.setState({
											stripeButtonEmailError: lang.MISSING_EMAIL
										});
									}}
								/>
							</React.Fragment>
						)
				  }
				: null
		].filter(method => !!method);

		if (!choices.length) {
			CK.logger.error('No payment methods available in form', { planId: this.props.planId });
		}

		return (
			<StripeProvider stripe={this.state.stripe || null}>
				<Elements
					fonts={[
						{
							family: 'Zona Pro',
							src: 'url(/fonts/ZonaPro-Regular.woff2)'
						}
					]}
				>
					<div className="PaymentForm">
						{this.state.showApplePayMessage && <p> {lang.APPLE_PAY_MESSAGE} </p>}
						{choices.length ? (
							<div className="PaymentForm__TabsWrapper">
								<TabbedSelector
									tabs={choices.map(choice => choice.tab)}
									contents={choices.map(choice => choice.content)}
									handleClick={billingMethod => this.setState({ billingMethod: billingMethod })}
									initialId={this.props.billingMethod || choices[0].id}
									theme={TabTheme.SWITCH}
								/>
							</div>
						) : (
							<p>{lang.NO_PAYMENT_METHODS}</p>
						)}
						{this.props.changeSubscriptionMessage && (
							<p className="topMessage changeSubscriptionMessage">
								{langToReactElements(this.props.changeSubscriptionMessage)}
							</p>
						)}
					</div>
				</Elements>
			</StripeProvider>
		);
	}
}

export const ConnectedPaymentForm = withAppContext(PaymentForm);
